import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { api, downloadJobResult } from '../api.js'
import { Card, Field, FileField, Alert, StatusPill, JobProgress, useJobElapsed, usePolling, UserSearch } from '../lib.jsx'
import PageViewerEditor from './editor/PageViewerEditor.jsx'

const TABS = [
  { key: 'content', label: 'Treść' },
  { key: 'editor', label: 'Edytor' },
  { key: 'export', label: 'Eksport' },
  { key: 'versions', label: 'Wersje i historia' },
  { key: 'share', label: 'Udostępnianie' },
]

const KIND_PL = { heading: 'nagłówek', paragraph: 'akapit', quote: 'cytat', bullet_list: 'lista punktowana',
  numbered_list: 'lista numerowana', table: 'tabela', image: 'obraz', chart: 'wykres' }

function useJob(jobId, opts) {
  return usePolling(api.job, jobId, opts)
}

// ---- Krok „Treść”: import pliku/tekstu + lista jednostek Document Modelu ----
// `importJobId`/`setImportJobId` są PODNIESIONE do rodzica (ProjectWorkspace), nie żyją tu lokalnie:
// to pozwala rodzicowi (a) wznowić polowanie po odświeżeniu strony (mount effect woła
// `api.activeJob`, ustawia ten sam id — `usePolling` podejmuje wątek jakby nic się nie stało), (b)
// wiedzieć że trwa aktywny job (gating zakładek), bez duplikowania stanu w dwóch miejscach.
function ContentTab({ projectId, units, reloadUnits, importJobId, setImportJobId }) {
  const [file, setFile] = useState(null)
  const [text, setText] = useState('')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)

  const importJob = useJob(importJobId, { onDone: () => { setImportJobId(null); reloadUnits() } })

  const confirmReplace = () => {
    if (units.length === 0) return true
    return window.confirm(`Import nowego pliku USUNIE i zastąpi całą obecną treść projektu (${units.length} jednostek). Kontynuować?`)
  }

  const submitFile = async (e) => {
    e.preventDefault()
    if (!file) return
    if (!confirmReplace()) return
    setErr(''); setBusy(true)
    try {
      const fd = new FormData(); fd.append('file', file)
      const res = await api.importFile(projectId, fd)
      setImportJobId(res.job_id); setFile(null)
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  const submitText = async (e) => {
    e.preventDefault()
    if (!text.trim()) return
    if (!confirmReplace()) return
    setErr(''); setBusy(true)
    try {
      const res = await api.importText(projectId, { text, format: 'text' })
      setImportJobId(res.job_id); setText('')
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  const running = importJob && importJob.status === 'running'
  const importElapsed = useJobElapsed(importJob)

  return (
    <div className="grid grid-2" style={{ alignItems: 'start' }}>
      <Card title="Import treści" desc="Zaimportuj plik (DOCX/PDF/TXT/MD/HTML) lub wklej tekst — powstanie Document Model gotowy do składu.">
        <Alert kind="err">{err}</Alert>
        {(importJobId || running) && (
          <Alert kind="info">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <span><span className="spin-sm" style={{ marginRight: 8 }} />Import w toku (analiza AI treści)… {importJob?.status && <StatusPill status={importJob.status} />}</span>
              <span className="mono small">{importElapsed}</span>
            </div>
          </Alert>
        )}
        <JobProgress job={importJob} showTime={false} />
        <form onSubmit={submitFile} style={{ marginBottom: 22 }}>
          <FileField label="Plik" accept=".docx,.pdf,.txt,.md,.html" file={file} onPick={setFile} />
          <button className="btn btn-primary" disabled={busy || !file}>{busy ? 'Wysyłanie…' : 'Importuj plik'}</button>
        </form>
        <div className="divider" />
        <form onSubmit={submitText}>
          <Field label="…albo wklej tekst"><textarea value={text} onChange={e => setText(e.target.value)} rows={6} placeholder="Wklej treść dokumentu (Markdown wspierany)…" /></Field>
          <button className="btn" disabled={busy || !text.trim()}>{busy ? 'Wysyłanie…' : 'Importuj tekst'}</button>
        </form>
      </Card>

      <Card title="Document Model" desc={`${units.length} jednostek treści`}>
        {units.length === 0 ? <div className="empty">Brak jeszcze treści — zaimportuj po lewej.</div> : (
          <div style={{ maxHeight: 520, overflowY: 'auto' }}>
            {units.map(u => {
              const c = u.content || {}
              return (
                <div key={u.id} className="unit-row">
                  <span className="kind-tag">{KIND_PL[u.kind] || u.kind}{u.level ? ` · H${u.level}` : ''}</span>
                  <div className="small" style={{ marginTop: 4 }}>
                    {c.text || (c.items || []).join(' · ') ||
                      (u.kind === 'table' ? `${(c.headers || []).length} kolumn × ${(c.rows || []).length} wierszy` :
                      (u.kind === 'image' ? c.image_ref : (c.title || 'bez podglądu')))}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Card>
    </div>
  )
}

// ---- Krok „Eksport” ----
function ExportTab({ projectId, hasPages }) {
  const [jobs, setJobs] = useState({}) // format -> job_id
  const [err, setErr] = useState('')

  const FORMATS = [
    { key: 'pdf', label: 'PDF', ext: 'pdf', desc: 'Pełny dokument / prezentacja PDF (wg typu szablonu).' },
    { key: 'pptx_pixel', label: 'PPTX (pixel-perfect)', ext: 'pptx', desc: 'Zrzut każdej strony jako obraz — wygląd 1:1 z podglądem.' },
    { key: 'pptx_editable', label: 'PPTX (edytowalny)', ext: 'pptx', desc: 'Natywne obiekty PowerPoint tam, gdzie to możliwe (fallback do obrazu per slajd).' },
  ]

  const start = async (fmt) => {
    setErr('')
    try { const res = await api.exportProject(projectId, fmt); setJobs(j => ({ ...j, [fmt]: res.job_id })) }
    catch (e) { setErr(e.message) }
  }

  return (
    <div className="grid grid-3">
      <Alert kind="err">{err}</Alert>
      {!hasPages && <Alert kind="warn">Projekt nie ma jeszcze złożonych stron — najpierw uruchom kompozycję w zakładce „Edytor”.</Alert>}
      {FORMATS.map(f => (
        <ExportCard key={f.key} format={f} jobId={jobs[f.key]} disabled={!hasPages} onStart={() => start(f.key)} />
      ))}
    </div>
  )
}
function ExportCard({ format, jobId, disabled, onStart }) {
  const job = useJob(jobId, { onDone: (j) => downloadJobResult(j.id, `eksport.${format.ext}`) })
  const running = job && (job.status === 'queued' || job.status === 'running')
  return (
    <Card title={format.label} desc={format.desc}>
      {job && <div style={{ marginBottom: 10 }}><StatusPill status={job.status} /></div>}
      <button className="btn btn-primary" disabled={disabled || running} onClick={onStart}>
        {running ? 'Generowanie…' : (job?.status === 'done' ? 'Wygeneruj ponownie' : 'Eksportuj')}
      </button>
      {job?.status === 'error' && <Alert kind="err">{job.error_msg}</Alert>}
    </Card>
  )
}

// ---- Krok „Wersje i historia” ----
function VersionsTab({ projectId }) {
  const [versions, setVersions] = useState([])
  const [history, setHistory] = useState([])
  const [label, setLabel] = useState('')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)

  const load = () => { api.versions(projectId).then(setVersions).catch(e => setErr(e.message)); api.history(projectId).then(setHistory).catch(() => {}) }
  useEffect(load, [projectId])

  const save = async () => {
    setBusy(true); setErr('')
    try { await api.saveVersion(projectId, label); setLabel(''); load() } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  const restore = async (v) => {
    setBusy(true); setErr('')
    try { await api.restoreVersion(projectId, v); load() } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  return (
    <div className="grid grid-2" style={{ alignItems: 'start' }}>
      <Card title="Wersje projektu">
        <Alert kind="err">{err}</Alert>
        <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
          <input placeholder="Etykieta wersji (opcjonalnie)" value={label} onChange={e => setLabel(e.target.value)} />
          <button className="btn btn-primary" disabled={busy} onClick={save}>Zapisz wersję</button>
        </div>
        {versions.length === 0 ? <div className="empty">Brak zapisanych wersji.</div> : (
          <table>
            <thead><tr><th>Wersja</th><th>Etykieta</th><th>Data</th><th /></tr></thead>
            <tbody>
              {versions.map(v => (
                <tr key={v.id}>
                  <td className="mono">v{v.version}</td>
                  <td>{v.label || '—'}</td>
                  <td className="muted small">{new Date(v.created_at).toLocaleString('pl-PL')}</td>
                  <td><button className="btn btn-sm btn-ghost" disabled={busy} onClick={() => restore(v.version)}>Przywróć</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Historia zmian" desc="Najnowsze pierwsze">
        {history.length === 0 ? <div className="empty">Brak historii.</div> : (
          <div style={{ maxHeight: 480, overflowY: 'auto' }}>
            {history.map(h => (
              <div key={h.id} className="kvp">
                <span>{h.description}</span>
                <span className="muted small">{new Date(h.created_at).toLocaleString('pl-PL')}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}

// ---- Krok „Udostępnianie” ----
function ShareTab({ projectId }) {
  const [shares, setShares] = useState([])
  const [picked, setPicked] = useState(null)
  const [role, setRole] = useState('viewer')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)

  const load = () => api.shares(projectId).then(setShares).catch(e => setErr(e.message))
  useEffect(() => { load() }, [projectId])

  const add = async () => {
    if (!picked) return
    setBusy(true); setErr('')
    try { await api.addShare(projectId, picked.id, role); setPicked(null); load() }
    catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  const remove = async (id) => {
    try { await api.removeShare(projectId, id); load() } catch (e) { setErr(e.message) }
  }

  return (
    <Card title="Udostępnianie projektu" desc="Znajdź użytkownika po nazwie lub e-mailu i przydziel rolę Editor lub Viewer.">
      <Alert kind="err">{err}</Alert>
      <div className="row" style={{ marginBottom: 18, alignItems: 'flex-start' }}>
        <div style={{ flex: 2, minWidth: 220 }}>
          {picked ? (
            <div className="unit-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span><b>{picked.username}</b> <span className="muted small">{picked.email}</span></span>
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => setPicked(null)}>Zmień</button>
            </div>
          ) : (
            <UserSearch onPick={setPicked} exclude={shares.map(s => s.user_id)} />
          )}
        </div>
        <select value={role} onChange={e => setRole(e.target.value)} style={{ maxWidth: 160 }}>
          <option value="viewer">Viewer</option>
          <option value="editor">Editor</option>
        </select>
        <button className="btn btn-primary" disabled={busy || !picked} onClick={add}>Udostępnij</button>
      </div>
      {shares.length === 0 ? <div className="empty">Projekt nie jest jeszcze udostępniony nikomu.</div> : (
        <table>
          <thead><tr><th>Użytkownik</th><th>Rola</th><th /></tr></thead>
          <tbody>
            {shares.map(s => (
              <tr key={s.id}>
                <td><b>{s.username}</b><div className="muted small">{s.email}</div></td>
                <td><span className="role-tag">{s.role}</span></td>
                <td><button className="btn btn-sm btn-danger" onClick={() => remove(s.id)}>Usuń</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  )
}

export default function ProjectWorkspace() {
  const { id } = useParams()
  const nav = useNavigate()
  const [project, setProject] = useState(null)
  const [units, setUnits] = useState([])
  const [pages, setPages] = useState([])
  const [tab, setTab] = useState('content')
  const [err, setErr] = useState('')
  const [dupBusy, setDupBusy] = useState(false)
  const [delBusy, setDelBusy] = useState(false)
  // Id jobów import/kompozycja PODNIESIONE tutaj (nie lokalnie w ContentTab/PageViewerEditor) —
  // to jedyny sposób, żeby po odświeżeniu strony (mount effect niżej, `api.activeJob`) wznowić
  // dokładnie ten sam job w dziecku ORAZ jednocześnie wiedzieć w rodzicu, że trwa aktywne zadanie
  // (gating zakładek poniżej), bez trzymania tego samego stanu w dwóch miejscach.
  const [importJobId, setImportJobId] = useState(null)
  const [composeJobId, setComposeJobId] = useState(null)

  const reloadUnits = () => api.units(id).then(setUnits).catch(e => setErr(e.message))
  const reloadPagesCount = () => api.pages(id).then(setPages).catch(() => {})

  useEffect(() => {
    api.project(id).then(setProject).catch(e => setErr(e.message))
    reloadUnits(); reloadPagesCount()
    // Wznowienie po odświeżeniu strony (Faza 10): jeśli w chwili odświeżenia trwał import/
    // kompozycja, podejmij dokładnie ten sam job — 404 (brak aktywnego joba) to normalny stan,
    // `api.activeJob` już zwraca wtedy null, nic więcej nie robimy.
    api.activeJob(id).then(job => {
      if (!job) return
      if (job.type === 'pdfgen_import') setImportJobId(job.id)
      else if (job.type === 'pdfgen_compose') setComposeJobId(job.id)
    }).catch(() => {})
  }, [id])

  const duplicate = async () => {
    setDupBusy(true)
    try { const d = await api.duplicateProject(id); nav(`/projects/${d.id}`) } catch (e) { setErr(e.message) } finally { setDupBusy(false) }
  }

  const remove = async () => {
    if (!window.confirm(`Usunąć projekt „${project.name}” bezpowrotnie? Tej operacji nie można cofnąć.`)) return
    setDelBusy(true)
    try { await api.deleteProject(id); nav('/') } catch (e) { setErr(e.message); setDelBusy(false) }
  }

  if (err && !project) return <div className="content"><Alert kind="err">{err}</Alert></div>
  if (!project) return <div className="content"><div className="empty">Wczytywanie…</div></div>

  // Gating zakładek (Faza 10): bez treści nie ma co edytować/eksportować; podczas aktywnego
  // importu/kompozycji reszta zakładek czeka, żeby nie operować na danych w trakcie zmiany.
  // `activeJobBusy` opiera się na tym samym podniesionym stanie co wznowienie polowania —
  // oba dziecko-komponenty czyszczą go (`setImportJobId(null)`/`setComposeJobId(null)`) w `onDone`.
  const hasContent = units.length > 0
  const activeJobBusy = importJobId != null || composeJobId != null

  return (
    <>
      <div className="topbar">
        <div>
          <button className="btn btn-sm btn-ghost" style={{ marginBottom: 10 }} onClick={() => nav('/')}>‹ Projekty</button>
          <h1>{project.name}</h1>
          <div className="sub">Status: {project.status} · wersja {project.current_version}</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn" disabled={dupBusy} onClick={duplicate}>{dupBusy ? 'Duplikowanie…' : 'Duplikuj'}</button>
          <button className="btn btn-danger" disabled={delBusy} onClick={remove}>{delBusy ? 'Usuwanie…' : 'Usuń projekt'}</button>
        </div>
      </div>
      <div className="content">
        <Alert kind="err">{err}</Alert>
        <div className="tabs">
          {TABS.map(t => {
            // Zakładka „Treść” jest ZAWSZE dostępna (nigdy nie blokujemy użytkownika całkowicie) —
            // to tam widoczny jest JobProgress/błąd aktywnego zadania, więc de facto pełni też rolę
            // jedynego dostępnego „anuluj/przerwij” UI dopóki backend nie ma endpointu do anulowania
            // jobów (nie istnieje dziś — poza zakresem tej fazy, patrz raport).
            const disabled = t.key !== 'content' && (!hasContent || activeJobBusy)
            const title = disabled
              ? (!hasContent ? 'Najpierw zaimportuj treść w zakładce „Treść”.' : 'Poczekaj na zakończenie bieżącego zadania (import/kompozycja) — postęp widoczny w zakładce „Treść”.')
              : undefined
            return (
              <div key={t.key} className={`tab ${tab === t.key ? 'active' : ''} ${disabled ? 'tab-disabled' : ''}`}
                title={title} onClick={() => { if (disabled) return; setTab(t.key) }}>{t.label}</div>
            )
          })}
        </div>
        {tab === 'content' && (
          <ContentTab projectId={id} units={units} reloadUnits={() => { reloadUnits(); reloadPagesCount() }}
            importJobId={importJobId} setImportJobId={setImportJobId} />
        )}
        {tab === 'editor' && (
          <PageViewerEditor projectId={id} units={units} reloadAll={() => { reloadUnits(); reloadPagesCount() }}
            composeJobId={composeJobId} setComposeJobId={setComposeJobId} />
        )}
        {tab === 'export' && <ExportTab projectId={id} hasPages={pages.length > 0} />}
        {tab === 'versions' && <VersionsTab projectId={id} />}
        {tab === 'share' && <ShareTab projectId={id} />}
      </div>
    </>
  )
}
